import { z } from 'zod';
import {
  MESSAGING_LIMITS,
  PRODUCTIVITY_LIMITS,
  reminderPresetSchema,
  reminderStatusSchema,
  savedItemStatusSchema,
  scheduledMessageStatusSchema,
  sidebarSectionShowSchema,
  sidebarSectionSortSchema,
  statusClearAfterSchema,
} from '../enums';
import { richTextDocWireSchema } from '../richtext/schema';
import { isoDateTimeSchema, singleLineText, uuidSchema } from './common';
import { messageDtoSchema } from './messaging';
import { sidebarSectionDtoSchema } from './sidebar';
import { pageInfoSchema, paginated } from './pagination';

/**
 * Personal productivity contracts (PLAN §4 "Sidebar sections, saved (Later), reminders, scheduled
 * messages, drafts", "People: … status, DND"; SPEC §4.2, §4.3, §4.5). Owned by the
 * `productivity` API module (apps/api/src/modules/productivity) and the web extras stream
 * (features/later, reminders, scheduled, drafts, status) plus features/sections (navigation).
 */

const emojiShortcode = z.string().regex(/^[a-z0-9_+-]{1,64}(::skin-tone-[2-6])?$/, 'Unknown emoji');

// ================================================================ sidebar sections (D-047, D-176)

export const sectionIdParamsSchema = z.object({ sectionId: uuidSchema });
export type SectionIdParams = z.infer<typeof sectionIdParamsSchema>;

/** GET /sidebar/sections (also `bootstrap.sections`). */
export const sidebarSectionListResponseSchema = z.object({
  items: z.array(sidebarSectionDtoSchema),
  nextCursor: z.null(),
  page: pageInfoSchema.optional(),
});
export type SidebarSectionListResponse = z.infer<typeof sidebarSectionListResponseSchema>;

export const createSectionRequestSchema = z.strictObject({
  name: singleLineText(1, PRODUCTIVITY_LIMITS.sectionNameChars),
  emoji: emojiShortcode.nullable().optional(),
});
export type CreateSectionRequest = z.infer<typeof createSectionRequestSchema>;

export const updateSectionRequestSchema = z.strictObject({
  name: singleLineText(1, PRODUCTIVITY_LIMITS.sectionNameChars).optional(),
  emoji: emojiShortcode.nullable().optional(),
  sort: sidebarSectionSortSchema.optional(),
  show: sidebarSectionShowSchema.optional(),
  collapsed: z.boolean().optional(),
});
export type UpdateSectionRequest = z.infer<typeof updateSectionRequestSchema>;

/**
 * PUT /sidebar/sections/order — every section id (custom and system rows) in the new order
 * (`422 order_mismatch` when the set differs).
 */
export const reorderSectionsRequestSchema = z.strictObject({
  sectionIds: z
    .array(uuidSchema)
    .min(1)
    .max(PRODUCTIVITY_LIMITS.maxCustomSections + 5),
});
export type ReorderSectionsRequest = z.infer<typeof reorderSectionsRequestSchema>;

/** PUT /sidebar/sections/:sectionId/conversations — each must be one of mine (404 otherwise). */
export const setSectionConversationsRequestSchema = z.strictObject({
  conversationIds: z.array(uuidSchema).max(500),
});
export type SetSectionConversationsRequest = z.infer<typeof setSectionConversationsRequestSchema>;

// ================================================================ Later (saved items)

export const savedItemIdParamsSchema = z.object({ savedItemId: uuidSchema });
export type SavedItemIdParams = z.infer<typeof savedItemIdParamsSchema>;

export const savedItemDtoSchema = z.object({
  id: uuidSchema,
  message: messageDtoSchema,
  conversationId: uuidSchema,
  status: savedItemStatusSchema,
  remindAt: isoDateTimeSchema.nullable(),
  completedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
});
export type SavedItemDto = z.infer<typeof savedItemDtoSchema>;

export const listSavedQuerySchema = z.object({
  status: savedItemStatusSchema.default('in_progress'),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListSavedQuery = z.infer<typeof listSavedQuerySchema>;

/** GET /saved — items of conversations the viewer can no longer read are left out. */
export const savedListResponseSchema = paginated(savedItemDtoSchema);
export type SavedListResponse = z.infer<typeof savedListResponseSchema>;

/** POST /saved — idempotent per message (`201` new, `200` existing). */
export const createSavedRequestSchema = z.strictObject({
  messageId: uuidSchema,
  remindAt: isoDateTimeSchema.nullable().optional(),
});
export type CreateSavedRequest = z.infer<typeof createSavedRequestSchema>;

export const updateSavedRequestSchema = z.strictObject({
  remindAt: isoDateTimeSchema.nullable().optional(),
  status: savedItemStatusSchema.optional(),
});
export type UpdateSavedRequest = z.infer<typeof updateSavedRequestSchema>;

// ================================================================ reminders

export const reminderIdParamsSchema = z.object({ reminderId: uuidSchema });
export type ReminderIdParams = z.infer<typeof reminderIdParamsSchema>;

/**
 * The reminder subset of the shared `RecurrenceRule` (D-057): daily, weekly (on ISO weekdays) or
 * monthly, every `interval` periods, in the reminder's captured timezone.
 */
export const reminderRecurrenceSchema = z.strictObject({
  v: z.literal(1),
  freq: z.enum(['daily', 'weekly', 'monthly']),
  interval: z.number().int().min(1).max(12),
  byWeekday: z.array(z.number().int().min(1).max(7)).min(1).max(7).optional(),
});
export type ReminderRecurrence = z.infer<typeof reminderRecurrenceSchema>;

export const reminderDtoSchema = z.object({
  id: uuidSchema,
  /** The person reminded. */
  userId: uuidSchema,
  createdByUserId: uuidSchema,
  text: z.string().nullable(),
  messageId: uuidSchema.nullable(),
  savedItemId: uuidSchema.nullable(),
  remindAt: isoDateTimeSchema,
  recurrence: reminderRecurrenceSchema.nullable(),
  timezone: z.string(),
  status: reminderStatusSchema,
  firedCount: z.number().int().nonnegative(),
  lastFiredAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
});
export type ReminderDto = z.infer<typeof reminderDtoSchema>;

export const listRemindersQuerySchema = z.object({
  status: z.enum(['pending', 'completed', 'all']).default('pending'),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListRemindersQuery = z.infer<typeof listRemindersQuerySchema>;

export const reminderListResponseSchema = paginated(reminderDtoSchema);
export type ReminderListResponse = z.infer<typeof reminderListResponseSchema>;

/** POST /reminders/parse — deterministic `chrono-node` in the workspace timezone, never AI. */
export const parseReminderRequestSchema = z.strictObject({
  text: singleLineText(1, PRODUCTIVITY_LIMITS.reminderTextChars),
});
export type ParseReminderRequest = z.infer<typeof parseReminderRequestSchema>;

export const parseReminderResponseSchema = z.object({
  remindAt: isoDateTimeSchema,
  /** What to remind about, with the time expression removed. */
  text: z.string(),
  recurrence: reminderRecurrenceSchema.nullable(),
});
export type ParseReminderResponse = z.infer<typeof parseReminderResponseSchema>;

/**
 * POST /reminders — exactly one of `remindAt` and `in` (the "Remind me about this" presets), and
 * at least one of `text` and `messageId`. `forUserId`: someone I share a DM with, never a guest.
 */
export const createReminderRequestSchema = z
  .strictObject({
    text: singleLineText(1, PRODUCTIVITY_LIMITS.reminderTextChars).optional(),
    messageId: uuidSchema.optional(),
    remindAt: isoDateTimeSchema.optional(),
    in: reminderPresetSchema.optional(),
    recurrence: reminderRecurrenceSchema.optional(),
    forUserId: uuidSchema.optional(),
  })
  .refine((b) => (b.remindAt === undefined) !== (b.in === undefined), {
    message: 'Send exactly one of remindAt and in',
    path: ['remindAt'],
  })
  .refine((b) => b.text !== undefined || b.messageId !== undefined, {
    message: 'Say what to remind about (text or messageId)',
    path: ['text'],
  });
export type CreateReminderRequest = z.infer<typeof createReminderRequestSchema>;

export const updateReminderRequestSchema = z.strictObject({
  text: singleLineText(1, PRODUCTIVITY_LIMITS.reminderTextChars).nullable().optional(),
  remindAt: isoDateTimeSchema.optional(),
});
export type UpdateReminderRequest = z.infer<typeof updateReminderRequestSchema>;

export const snoozeReminderRequestSchema = z.strictObject({ until: isoDateTimeSchema });
export type SnoozeReminderRequest = z.infer<typeof snoozeReminderRequestSchema>;

// ================================================================ scheduled messages

export const scheduledMessageIdParamsSchema = z.object({ scheduledMessageId: uuidSchema });
export type ScheduledMessageIdParams = z.infer<typeof scheduledMessageIdParamsSchema>;

export const scheduledMessageDtoSchema = z.object({
  id: uuidSchema,
  conversationId: uuidSchema,
  threadRootId: uuidSchema.nullable(),
  alsoSendToChannel: z.boolean(),
  content: richTextDocWireSchema,
  plainText: z.string(),
  fileIds: z.array(uuidSchema),
  sendAt: isoDateTimeSchema,
  status: scheduledMessageStatusSchema,
  /** e.g. `not_conversation_member`, `conversation_archived`, `file_not_attachable`. */
  failureCode: z.string().nullable(),
  sentMessageId: uuidSchema.nullable(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type ScheduledMessageDto = z.infer<typeof scheduledMessageDtoSchema>;

export const listScheduledQuerySchema = z.object({
  conversationId: uuidSchema.optional(),
  status: scheduledMessageStatusSchema.optional(),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListScheduledQuery = z.infer<typeof listScheduledQuerySchema>;

export const scheduledMessageListResponseSchema = paginated(scheduledMessageDtoSchema);
export type ScheduledMessageListResponse = z.infer<typeof scheduledMessageListResponseSchema>;

/**
 * POST /conversations/:conversationId/scheduled-messages — `Idempotency-Key` required; `content`
 * or `text` (D-045); `sendAt` from now + 1 minute to now + 120 days (`422 send_at_out_of_range`).
 */
export const createScheduledMessageRequestSchema = z.strictObject({
  content: richTextDocWireSchema.optional(),
  text: z
    .string()
    .max(MESSAGING_LIMITS.maxPlainTextChars * 2)
    .optional(),
  threadRootId: uuidSchema.optional(),
  alsoSendToChannel: z.boolean().optional(),
  fileIds: z.array(uuidSchema).max(MESSAGING_LIMITS.maxFilesPerMessage).optional(),
  sendAt: isoDateTimeSchema,
});
export type CreateScheduledMessageRequest = z.infer<typeof createScheduledMessageRequestSchema>;

/** PATCH /scheduled-messages/:id — only while `scheduled` (`409 scheduled_message_not_editable`). */
export const updateScheduledMessageRequestSchema = z.strictObject({
  content: richTextDocWireSchema.optional(),
  text: z
    .string()
    .max(MESSAGING_LIMITS.maxPlainTextChars * 2)
    .optional(),
  sendAt: isoDateTimeSchema.optional(),
});
export type UpdateScheduledMessageRequest = z.infer<typeof updateScheduledMessageRequestSchema>;

// ================================================================ drafts and sent

export const draftDtoSchema = z.object({
  conversationId: uuidSchema,
  threadRootId: uuidSchema.nullable(),
  content: richTextDocWireSchema,
  plainText: z.string(),
  fileIds: z.array(uuidSchema),
  clientUpdatedAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type DraftDto = z.infer<typeof draftDtoSchema>;

/** GET /drafts — newest first. */
export const draftListResponseSchema = paginated(draftDtoSchema);
export type DraftListResponse = z.infer<typeof draftListResponseSchema>;

/**
 * PUT /conversations/:conversationId/draft — last writer wins on `clientUpdatedAt`
 * (`409 draft_stale` when a newer one is stored). Empty content and no files deletes the draft
 * (`204`).
 */
export const putDraftRequestSchema = z.strictObject({
  content: richTextDocWireSchema,
  fileIds: z.array(uuidSchema).max(MESSAGING_LIMITS.maxFilesPerMessage).optional(),
  threadRootId: uuidSchema.nullable().optional(),
  clientUpdatedAt: isoDateTimeSchema,
});
export type PutDraftRequest = z.infer<typeof putDraftRequestSchema>;

/** DELETE /conversations/:conversationId/draft?threadRootId= */
export const deleteDraftQuerySchema = z.object({ threadRootId: uuidSchema.optional() });
export type DeleteDraftQuery = z.infer<typeof deleteDraftQuerySchema>;

/** GET /me/sent-messages — the Sent tab of Drafts & sent. */
export const sentMessagesQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type SentMessagesQuery = z.infer<typeof sentMessagesQuerySchema>;
export const sentMessagesResponseSchema = paginated(messageDtoSchema);
export type SentMessagesResponse = z.infer<typeof sentMessagesResponseSchema>;

// ================================================================ custom status, DND, schedule

export const customStatusDtoSchema = z.object({
  emoji: z.string().nullable(),
  text: z.string().nullable(),
  /** Null = does not clear by itself. Read paths treat a passed expiry as no status. */
  expiresAt: isoDateTimeSchema.nullable(),
});
export type CustomStatusDto = z.infer<typeof customStatusDtoSchema>;

/** PUT /me/status — `expiresAt` or `clearAfter` (computed in my timezone), not both. */
export const setStatusRequestSchema = z
  .strictObject({
    emoji: emojiShortcode.nullable().optional(),
    text: singleLineText(0, PRODUCTIVITY_LIMITS.statusTextChars).nullable().optional(),
    expiresAt: isoDateTimeSchema.nullable().optional(),
    clearAfter: statusClearAfterSchema.optional(),
  })
  .refine((b) => !(b.expiresAt !== undefined && b.clearAfter !== undefined), {
    message: 'Send expiresAt or clearAfter, not both',
    path: ['clearAfter'],
  });
export type SetStatusRequest = z.infer<typeof setStatusRequestSchema>;

/** PUT /me/dnd — pause notifications until `until`. */
export const setDndRequestSchema = z.strictObject({ until: isoDateTimeSchema });
export type SetDndRequest = z.infer<typeof setDndRequestSchema>;

export const dndResponseSchema = z.object({ dndUntil: isoDateTimeSchema.nullable() });
export type DndResponse = z.infer<typeof dndResponseSchema>;

const hhmm = z
  .string()
  .regex(/^(?:(?:[01]\d|2[0-3]):[0-5]\d|24:00)$/, 'Use HH:mm (24 h; 24:00 as an end)');

/** One allowed window per weekday, `[start, end)` in my timezone. */
export const notificationScheduleDaySchema = z.strictObject({
  weekday: z.number().int().min(1).max(7),
  start: hhmm,
  end: hhmm,
});
export type NotificationScheduleDay = z.infer<typeof notificationScheduleDaySchema>;

/**
 * GET, PUT /me/notification-schedule — "notify me only 9 AM–8 PM, Mon–Sat". A weekday without an
 * entry has no allowed hours (`422 invalid_schedule` for duplicates or `end <= start`).
 */
export const notificationScheduleDtoSchema = z.object({
  enabled: z.boolean(),
  days: z.array(notificationScheduleDaySchema).max(7),
  /** The timezone the windows are read in (mine, else the workspace's). */
  timezone: z.string(),
});
export type NotificationScheduleDto = z.infer<typeof notificationScheduleDtoSchema>;

export const putNotificationScheduleRequestSchema = z.strictObject({
  enabled: z.boolean(),
  days: z.array(notificationScheduleDaySchema).max(7),
});
export type PutNotificationScheduleRequest = z.infer<typeof putNotificationScheduleRequestSchema>;
